import { logger } from '../utils/logger';
/**
 * Proactive Token Refresh Hook
 *
 * Periodically checks token expiration and attempts silent refresh using
 * MSAL's acquireTokenSilent as the canonical expiry signal.
 *
 * Microsoft best practice: SPAs must NOT decode access tokens client-side
 * (they are opaque strings). Instead, catch InteractionRequiredAuthError from
 * acquireTokenSilent — that is the authoritative signal that the session can
 * no longer be silently renewed.
 *
 * @see https://learn.microsoft.com/en-us/entra/identity-platform/scenario-spa-acquire-token
 */

import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import type { RootState } from '../store';
import {
  setTokenExpiredWithFailedRefresh,
  setLastTokenRefreshAttempt,
  setTokenExpiresOn,
} from '../store/slices/authSlice';
import { TOKEN_CHECK_INTERVAL_MS, REFRESH_ATTEMPT_COOLDOWN_MS } from '../utils/tokenExpiration';
import { getMsalInstance, tokenRequest } from '../config/authConfig';
import { isAuthRedirectInProgress } from '../utils/authRedirect';
import { setTokenExpiresOnCache } from '../utils/tokenCache';

/**
 * Hook to proactively refresh tokens before they expire.
 *
 * Behavior:
 * - Checks token by calling acquireTokenSilent (MSAL-native, no JWT decoding)
 * - Fires immediately on mount, every 60 seconds via interval, and on tab re-focus
 * - On InteractionRequiredAuthError: session cannot be silently renewed → show modal
 * - Prevents duplicate refresh attempts within 5-minute cooldown window
 * - Updates Redux tokenExpiresOn from MSAL's AuthenticationResult.expiresOn
 */
export const useTokenRefresh = () => {
  const dispatch = useDispatch();
  const { account, isAuthenticated, authReadyState } = useSelector(
    (state: RootState) => state.auth
  );
  const lastRefreshAttempt = useSelector((state: RootState) => state.auth.lastTokenRefreshAttempt);

  // Use a ref so the visibilitychange handler always sees the latest values
  // without being recreated every time they change.
  const stateRef = useRef({ account, isAuthenticated, authReadyState, lastRefreshAttempt });
  useEffect(() => {
    stateRef.current = { account, isAuthenticated, authReadyState, lastRefreshAttempt };
  });

  useEffect(() => {
    // Only run if user is fully authenticated and auth initialization is complete.
    // This prevents token acquisition attempts during redirect callback processing,
    // which can cause redirect loops when stale cached accounts exist.
    if (!account || !isAuthenticated || authReadyState !== 'ready') {
      return;
    }

    /**
     * Attempt a silent token acquisition.
     *
     * acquireTokenSilent is the Microsoft-recommended way to both check expiry AND
     * get a fresh token. MSAL internally respects tokenRenewalOffsetSeconds (300s),
     * so it will proactively reach out to Entra before the token actually expires.
     *
     * - Success: update tokenExpiresOn from the result's expiresOn timestamp
     * - InteractionRequiredAuthError: session cannot be renewed silently → show modal
     * - Other errors: transient (network, MSAL internal) → skip silently
     */
    const checkToken = async () => {
      const { lastRefreshAttempt: latestAttempt } = stateRef.current;

      // Skip if an auth redirect is already in flight
      if (isAuthRedirectInProgress()) {
        return;
      }

      // Prevent duplicate refresh attempts within cooldown period
      const now = Date.now();
      if (latestAttempt && now - latestAttempt < REFRESH_ATTEMPT_COOLDOWN_MS) {
        return;
      }

      try {
        const msalInstance = getMsalInstance();
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length === 0) {
          return;
        }

        dispatch(setLastTokenRefreshAttempt(now));

        const response = await msalInstance.acquireTokenSilent({
          ...tokenRequest,
          account: accounts[0]!,
          // forceRefresh is intentionally omitted here: MSAL will use the cache
          // when the token is still valid (respecting tokenRenewalOffsetSeconds),
          // and will reach out to Entra when the token is within the renewal window.
          // This avoids unnecessary network requests on every check interval.
        });

        // Update expiry timestamp in both Redux (for persistence/UI) and the
        // lightweight tokenCache bridge (for synchronous pre-flight checks in
        // api.client.ts without a circular store import).
        if (response.expiresOn) {
          const expiresOnMs = response.expiresOn.getTime();
          dispatch(setTokenExpiresOn(expiresOnMs));
          setTokenExpiresOnCache(expiresOnMs);
        }
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          // Session cannot be silently renewed: refresh token expired, user revoked,
          // Conditional Access policy changed, etc. Show the expiration modal.
          logger.warn('[useTokenRefresh] Session cannot be silently renewed', {
            errorCode: error.errorCode,
          });
          dispatch(setTokenExpiredWithFailedRefresh(true));
        }
        // All other errors (network glitch, MSAL transient) are non-fatal — skip.
      }
    };

    // Check immediately on mount
    checkToken();

    // Set up periodic check every 60 seconds
    const intervalId = setInterval(checkToken, TOKEN_CHECK_INTERVAL_MS);

    // Check immediately when the tab becomes visible again.
    // Without this, a user returning to a stale tab waits up to 60 seconds
    // before the interval fires and detects the expired session.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkToken();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // lastRefreshAttempt is tracked via stateRef to avoid recreating the interval
    // on every cooldown update.
  }, [account, isAuthenticated, authReadyState, dispatch]);
};
